import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CrewMerchantUi } from 'corso-types';
import api from '~/api';
import {
  SupportedClaimLineItem,
  SupportedResolvedResolutionMethod,
  useClaim,
} from '~/features/claimReview/ClaimReviewProvider';
import { useStoreId } from '~/providers/StoreUserProvider';
import { useClaimLedgerSummary } from './useClaimLedgerSummary';
import { claimOrderLineageQueryKey } from './useClaimOrderLineage';

type ClaimLineItemUpdate = CrewMerchantUi.ClaimLineItemUpdate;
export type ClaimLineItemUpdateApproval<
  T extends SupportedResolvedResolutionMethod =
    SupportedResolvedResolutionMethod,
> = Extract<
  Extract<ClaimLineItemUpdate, { kind: 'approved' }>['approval'],
  { resolutionMethod: `${T}` }
>;
export type ClaimLineItemUpdateDenial = Extract<
  ClaimLineItemUpdate,
  { kind: 'denied' }
>['denial'];

export type ClaimLineItemUpdateCancellation = Extract<
  ClaimLineItemUpdate,
  { kind: 'canceled' }
>['cancellation'];

export type ClaimLineItemUpdateReason = Extract<
  ClaimLineItemUpdate,
  { kind: 'reason' }
>['reason'];

// ? maybe rename as `useClaimLineItemUpdateMutation`
export default function useClaimLineItemUpdate(
  claimLineItemId: SupportedClaimLineItem['id'],
) {
  const storeId = useStoreId();
  const claim = useClaim();
  const queryClient = useQueryClient();
  const claimLedgerSummary = useClaimLedgerSummary();

  return useMutation({
    mutationFn: api
      .store(storeId)
      .claim(`${claim.id}`)
      .lineItem(`${claimLineItemId}`).update,
    onSettled: async () =>
      Promise.all([
        claim.refetch(),
        claimLedgerSummary.refetch(),
        // invalidate rather than refetch, so a store without the flag makes no request
        queryClient.invalidateQueries({
          queryKey: claimOrderLineageQueryKey(storeId, claim.id),
        }),
      ]),
  });
}
